<template>
  <div>
    <!-- Header Actions -->
    <div class="d-flex justify-content-end mb-2">
      <b-button variant="outline-secondary" class="mr-2">
        PayID Configurations
      </b-button>
      <b-button variant="primary">Generate PayID</b-button>
    </div>

    <!-- Filter Section -->
    <b-card>
      <b-row>
        <b-col cols="12" md="8">
          <b-row>
            <b-col md="4">
              <b-form-group label="Search...">
                <b-form-input
                  placeholder="Name / Email / Username"
                  v-model="filters.search"
                  @input="debouncedFetch"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Status">
                <b-form-select
                  :options="statusOptions"
                  v-model="filters.status"
                  @change="onSearch"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="User PayID">
                <b-form-input
                  placeholder="e.g. 12345@statetoken.com"
                  v-model="filters.userPayId"
                  @input="debouncedFetch"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>

        <b-col cols="4" class="mt-auto mb-1">
          <b-row>
            <b-col cols="12">
              <div class="d-flex justify-content-md-end mt-auto">
                <b-button
                  variant="primary"
                  class="mr-1 text-nowrap"
                >
                  Export
                </b-button>
                <b-button variant="outline-dark" @click="clearFilters"
                  >Clear</b-button
                >
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-card>

    <!-- PayID Table -->
    <b-card>
      <b-table
        :items="users"
        :fields="fields"
        :busy="loading"
        thead-class="text-nowrap"
        responsive
        show-empty
      >
        <!-- Custom cell: Full Name -->
        <template #cell(fullName)="data">
          <span>
            {{
              [data.item.firstName, data.item.middleName, data.item.lastName]
                .filter(Boolean)
                .join(" ")
            }}
          </span>
        </template>

        <template #cell(payId)="data">
          <span>
            {{ data.item.payId || "-" }}
          </span>
        </template>

        <template #cell(zeptoContactId)="data">
          <span>
            {{ data.item.zeptoContactId || "-" }}
          </span>
        </template>

        <!-- Custom cell: Status -->
        <template #cell(disablePayId)="data">
          <b-badge :variant="data.item.disablePayId ? 'danger' : 'success'">
            {{ data.item.disablePayId ? "Disabled" : "Active" }}
          </b-badge>
        </template>

        <!-- Custom cell: Actions -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center">
            <b-button
              size="sm"
              :variant="
                data.item.disablePayId ? 'outline-success' : 'outline-danger'
              "
              class="text-nowrap mr-1"
              :disabled="!!rowLoadingDisable[data.item.userId]"
              @click="handleTogglePayId(data.item)"
            >
              {{ data.item.disablePayId ? "Enable PayID" : "Disable PayID" }}
            </b-button>
            <b-link :to="`/customers/view/${data.item.userId}`">
              <b-button
                size="sm"
                variant="outline-secondary"
                class="text-nowrap"
                >View User</b-button
              >
            </b-link>
          </div>
        </template>
      </b-table>

      <div class="d-flex flex-wrap justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <span class="mr-1 text-nowrap">Rows per page</span>
          <b-form-select
            v-model="limit"
            :options="limitOptions"
            size="sm"
            class="mr-1"
          />
          <span class="text-nowrap">
            Showing {{ from }} – {{ to }} of {{ total }} entries
          </span>
        </div>

        <b-pagination
          v-model="page"
          :total-rows="total"
          :per-page="limit"
          first-number
          last-number
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BTable,
  BBadge,
  BLink,
  BPagination,
  BSpinner,
} from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import { debounce } from "debounce";
import ABA from "aba-generator";
import { formatDate } from "@/helpers/utils";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BTable,
    BBadge,
    BLink,
    BPagination,
    BSpinner,
  },
  data() {
    return {
      filters: {
        search: "",
        status: null,
        userPayId: "",
      },
      statusOptions: [
        { value: null, text: "Any" },
        { value: 0, text: "Active" },
        { value: 1, text: "Disabled" },
      ],
      fields: [
        { key: "email", label: "User ID (Email)" },
        { key: "fullName", label: "User Full Name" },
        { key: "payId", label: "User PayID" },
        { key: "zeptoContactId", label: "PayID Contact ID" },
        { key: "manualFlag", label: "Manual Flag" },
        { key: "disablePayId", label: "Status" },
        { key: "actions", label: "Actions", class: "text-right" },
      ],
      page: 1,
      limit: 25,
      limitOptions: [10, 25, 50, 100],
      rowLoadingDisable: {},
    };
  },
  computed: {
    ...mapState("finance/payId", ["users", "pagination", "loading"]),
    total() {
      return this.pagination.total >= 0 ? this.pagination.total : 0;
    },
    from() {
      if (!this.total) return 0;
      return this.limit * (this.page - 1) + 1;
    },
    to() {
      return Math.min(this.limit * this.page, this.total);
    },
  },
  watch: {
    page() {
      this.fetchUsers();
    },
    limit() {
      this.page = 1;
      this.fetchUsers();
    },
  },
  created() {
    this.debouncedFetch = debounce(this.onSearch, 300);
    this.fetchUsers();
  },
  methods: {
    ...mapActions("finance/payId", ["getPayIdUsers", "togglePayId"]),
    buildParams() {
      const params = { page: this.page, limit: this.limit };
      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.status !== null)
        params.disablePayId = this.filters.status;
      if (this.filters.userPayId) params.payId = this.filters.userPayId;
      return params;
    },
    async fetchUsers() {
      await this.getPayIdUsers(this.buildParams());
    },
    onSearch() {
      this.page = 1;
      this.fetchUsers();
    },
    clearFilters() {
      this.filters = { search: "", status: null, userPayId: "" };
      this.page = 1;
      this.fetchUsers();
    },
    async handleTogglePayId(user) {
      const { showToastSuccess } = useToastHandler();
      this.$set(this.rowLoadingDisable, user.userId, true);
      try {
        await this.togglePayId({
          userId: user.userId,
          disablePayId: !user.disablePayId,
        });
        const msg = user.disablePayId
          ? "PayID enabled successfully"
          : "PayID disabled successfully";
        showToastSuccess("Success", msg);
        this.fetchUsers();
      } finally {
        this.$set(this.rowLoadingDisable, user.userId, false);
      }
    },
    handleExportABA() {
      const { showToastError } = useToastHandler();
      if (!this.users.length) {
        showToastError("No data to export");
        return;
      }

      const pays = this.users
        .filter((item) => item.bsb && item.accountNumber)
        .map((item) => ({
          bsb: item.bsb,
          transactionCode: "50",
          account: (item?.accountNumber || "").replace(/\s/g, ""),
          amount: item.amount ? +Number(item.amount).toFixed(2) : 0,
          accountTitle:
            [item.firstName, item.lastName].filter(Boolean).join(" ") ||
            item.username ||
            "",
          reference: item.payId || "StakeToken",
          traceBsb: "084-472",
          traceAccount: "738301686",
          remitter: "StakeToken",
        }));

      if (!pays.length) {
        showToastError("No users with bank account details to export");
        return;
      }

      const totalAmount = pays.reduce((acc, p) => acc + p.amount, 0);
      const aba = new ABA({
        header: {
          bank: "NAB",
          user: "StakeToken Trading",
          userNumber: "000000",
          description: "StakeToken",
        },
        footer: {
          netTotal: "0000000000",
          debitTotal: +totalAmount.toFixed(2),
        },
      });

      const file = aba.generate(pays);
      const date = formatDate(new Date(), "yyyyMMdd_HHmm");
      const blob = new Blob([file], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `PayID_${date}.aba`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>
